import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import {
  Container, Card, CardContent, Typography, Button, Box,
  Grid, Alert, CircularProgress, Paper, Dialog, DialogTitle,
  DialogContent, DialogActions, Slider, IconButton
} from '@mui/material';
import {
  CameraAlt, Download, Refresh, Crop, RotateLeft, RotateRight,
  ZoomIn, ZoomOut, Close
} from '@mui/icons-material';
import { useAuth } from '../App';
import axios from 'axios';
import Cookies from 'js-cookie';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../utils/cropImage';

const API_BASE = API_BASE_URL;

const AlumniIDCardPage = () => {
  const { user } = useAuth();

  const [alumniData, setAlumniData] = useState(null);
  const [alumniID, setAlumniID] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [originalImage, setOriginalImage] = useState('');

  // Photo editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [cardPreview, setCardPreview] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Load alumni data on mount
  useEffect(() => {
    loadAlumniData();
  }, [user]);

  // Auto-generate card when alumni data is loaded
  useEffect(() => {
    if (alumniData && alumniID && !cardPreview) {
      console.log('[ID_CARD] Auto-generating initial card...');
      generatePreview(false);
    }
  }, [alumniData, alumniID]);

  const loadAlumniData = async () => {
    try {
      if (!user?.email) {
        setError('No user email found');
        setLoading(false);
        return;
      }

      const token = Cookies.get('idToken');
      console.log('[ID_CARD] Loading alumni data for:', user.email);

      const response = await axios.get(`${API_BASE}/alumni/${user.email}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      console.log('[ID_CARD] Alumni data loaded:', response.data);
      const data = response.data;
      const alumniId = data.AlumniID || '';

      if (!alumniId) {
        setError('Your AlumniID has not been assigned yet. Please contact the alumni office.');
        setLoading(false);
        return;
      }

      setAlumniID(alumniId);
      setAlumniData({
        fullName: data['FULL NAME'] || `${data.First_Name} ${data.Last_Name}`,
        degree: data['Degree Obtained'] || 'N/A',
        school: data.School || 'N/A',
        graduation: data.graduation || 'N/A',
        email: data['Email Contact'] || user.email,
        phone: data['Contact Number'] || 'N/A'
      });

      setLoading(false);

    } catch (err) {
      console.error('[ID_CARD] Error loading alumni data:', err);
      if (err.response?.status === 404) {
        setError('Alumni record not found or AlumniID not assigned.');
      } else {
        setError('Failed to load alumni data');
      }
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        setError('Only JPG and PNG images are allowed');
        return;
      }

      console.log('[ID_CARD] Photo uploaded:', file.name);

      const reader = new FileReader();
      reader.onloadend = () => {
        setOriginalImage(reader.result);
        setEditorOpen(true);
        // Reset editor state
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSaveCrop = async () => {
    try {
      console.log('[ID_CARD] Applying crop/rotation...');
      const croppedImage = await getCroppedImg(
        originalImage,
        croppedAreaPixels,
        rotation
      );

      console.log('[ID_CARD] Cropped image created');

      // Convert blob to file
      const response = await fetch(croppedImage);
      const blob = await response.blob();
      const file = new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' });

      setUploadedImage(file);
      setImagePreview(croppedImage);
      setEditorOpen(false);

      console.log('[ID_CARD] Photo saved, will regenerate card');
    } catch (err) {
      console.error('[ID_CARD] Crop error:', err);
      setError('Failed to process image');
    }
  };

  const handleCancelCrop = () => {
    setEditorOpen(false);
    setOriginalImage('');
  };

  // Auto-regenerate when image is uploaded
  useEffect(() => {
    if (uploadedImage && alumniData) {
      console.log('[ID_CARD] Photo changed, regenerating card with photo...');
      generatePreview(true);
    }
  }, [uploadedImage]);

  const generatePreview = async (withPhoto = false) => {
    try {
      console.log('[ID_CARD] Starting generation...');
      console.log('[ID_CARD] With photo:', withPhoto);

      setGenerating(true);
      setError('');

      const formData = new FormData();
      if (withPhoto && uploadedImage) {
        console.log('[ID_CARD] Attaching image:', uploadedImage.name);
        formData.append('profile_image', uploadedImage);
      }

      const token = Cookies.get('idToken');
      console.log('[ID_CARD] Calling:', `${API_BASE}/alumni/id-card/generate`);

      const response = await axios.post(
        `${API_BASE}/alumni/id-card/generate`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('[ID_CARD] Response received');

      if (response.data && response.data.image) {
        setCardPreview(response.data.image);
        console.log('[ID_CARD] ✅ Preview set successfully');
      } else {
        console.error('[ID_CARD] ❌ No image in response');
        setError('Failed to generate card - no image returned');
      }

      setGenerating(false);

    } catch (err) {
      console.error('[ID_CARD] ❌ Error:', err);
      console.error('[ID_CARD] Response:', err.response?.data);
      console.error('[ID_CARD] Status:', err.response?.status);

      setError(err.response?.data?.error || err.message || 'Failed to generate preview');
      setGenerating(false);
    }
  };

  const downloadCard = () => {
    if (!cardPreview) return;

    console.log('[ID_CARD] Downloading card...');
    const link = document.createElement('a');
    link.href = cardPreview;
    link.download = `USC_Alumni_ID_${alumniID}.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    console.log('[ID_CARD] ✅ Download triggered');
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading your alumni data...
        </Typography>
      </Container>
    );
  }

  if (error && !alumniData) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#046A38' }}>
        🎓 Your Alumni ID Card
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Left: Controls */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3, mb: 3, bgcolor: '#F6F9F5', border: '2px solid #046A38' }}>
            <Typography variant="caption" color="text.secondary">
              Your Alumni ID Number
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#046A38', my: 1 }}>
              {alumniID}
            </Typography>
          </Paper>

          <Card elevation={3} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                📸 Add Your Photo
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload a photo to personalize your ID card. You can crop and rotate it before adding.
              </Typography>

              <Button
                variant="contained"
                component="label"
                startIcon={<CameraAlt />}
                fullWidth
                sx={{
                  bgcolor: '#046A38',
                  '&:hover': { bgcolor: '#035028' },
                  py: 1.5,
                  mb: 2
                }}
              >
                {imagePreview ? 'Change Photo' : 'Upload Photo'}
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={handleImageUpload}
                />
              </Button>

              {imagePreview && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Current Photo:
                  </Typography>
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Preview"
                    sx={{
                      width: 150,
                      height: 150,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '4px solid #046A38',
                      mt: 1
                    }}
                  />
                  <Button
                    size="small"
                    startIcon={<Crop />}
                    onClick={() => {
                      setOriginalImage(imagePreview);
                      setEditorOpen(true);
                    }}
                    sx={{ mt: 1 }}
                  >
                    Re-crop Photo
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              startIcon={generating ? <CircularProgress size={20} /> : <Refresh />}
              onClick={() => generatePreview(!!uploadedImage)}
              disabled={generating}
              sx={{
                borderColor: '#046A38',
                color: '#046A38',
                fontWeight: 'bold',
                py: 1.5,
                '&:hover': { borderColor: '#035028', bgcolor: 'rgba(4, 106, 56, 0.04)' }
              }}
            >
              {generating ? 'Updating...' : 'Regenerate Card'}
            </Button>

            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<Download />}
              onClick={downloadCard}
              disabled={!cardPreview}
              sx={{
                bgcolor: '#FCCA18',
                color: '#046A38',
                fontWeight: 'bold',
                py: 2,
                fontSize: '1.1rem',
                '&:hover': { bgcolor: '#E6B515' },
                '&:disabled': { bgcolor: '#ccc', color: '#666' }
              }}
            >
              Download ID Card
            </Button>
          </Box>

          <Box sx={{ mt: 3, p: 2, bgcolor: '#F6F9F5', borderRadius: 2, border: '1px solid #046A38' }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#046A38' }}>
              💡 Quick Tips:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
              <li>Photo is optional</li>
              <li>Crop & rotate before saving</li>
              <li>Card auto-updates when you upload</li>
              <li>High-quality, print-ready PNG</li>
            </Box>
          </Box>
        </Grid>

        {/* Right: Preview */}
        <Grid item xs={12} md={8}>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {cardPreview ? '✅ Your ID Card' : '⏳ Generating...'}
                </Typography>
                {generating && <CircularProgress size={24} />}
              </Box>

              {cardPreview ? (
                <Box
                  sx={{
                    p: 3,
                    bgcolor: '#f5f5f5',
                    borderRadius: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Box
                    component="img"
                    src={cardPreview}
                    alt="ID Card Preview"
                    sx={{
                      width: '100%',
                      maxWidth: 900,
                      height: 'auto',
                      border: '2px solid #046A38',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                      borderRadius: 1
                    }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 8,
                    bgcolor: '#f5f5f5',
                    borderRadius: 2,
                    textAlign: 'center',
                    border: '2px dashed #ccc'
                  }}
                >
                  <CircularProgress sx={{ mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Generating your ID card...
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    This will only take a moment
                  </Typography>
                </Box>
              )}

              {cardPreview && !imagePreview && (
                <Alert severity="info" sx={{ mt: 3 }}>
                  💡 Upload a photo to add your picture to the ID card!
                </Alert>
              )}

              {cardPreview && imagePreview && (
                <Alert severity="success" sx={{ mt: 3 }}>
                  ✅ Your ID card is ready with your photo! Click "Download ID Card" to save it.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Photo Editor Dialog */}
      <Dialog
        open={editorOpen}
        onClose={handleCancelCrop}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Crop & Rotate Photo</Typography>
            <IconButton onClick={handleCancelCrop} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ position: 'relative', width: '100%', height: 400, bgcolor: '#000', borderRadius: 1 }}>
            <Cropper
              image={originalImage}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" gutterBottom>Zoom</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ZoomOut />
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(e, value) => setZoom(value)}
                sx={{ flex: 1 }}
              />
              <ZoomIn />
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" gutterBottom>Rotation</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={() => setRotation(r => r - 90)}>
                <RotateLeft />
              </IconButton>
              <Slider
                value={rotation}
                min={0}
                max={360}
                step={1}
                onChange={(e, value) => setRotation(value)}
                sx={{ flex: 1 }}
              />
              <IconButton onClick={() => setRotation(r => r + 90)}>
                <RotateRight />
              </IconButton>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCrop}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveCrop}
            sx={{ bgcolor: '#046A38', '&:hover': { bgcolor: '#035028' } }}
          >
            Apply & Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AlumniIDCardPage;